// Profile API service for managing user profiles

export interface ProfileData {
  id?: number
  user_id?: number
  profile_picture_url?: string
  bio?: string
  location?: string
  phone?: string
  website?: string
  resume_url?: string
  
  // Social media links
  linkedin_url?: string
  twitter_url?: string
  instagram_url?: string
  facebook_url?: string
  
  // Coding profiles
  leetcode_url?: string
  hackerrank_url?: string
  codeforces_url?: string
  codechef_url?: string
  
  // Open source contributions
  github_url?: string
  gitlab_url?: string
  bitbucket_url?: string
  
  created_at?: string
  updated_at?: string
}

export interface ProfileCompletion {
  completion_percentage: number
  completed_fields: number
  total_fields: number
  missing_fields: string[]
}

export interface ProfilePictureResponse {
  profile_picture_url: string
  message: string
}

class ProfileAPI {
  private baseURL = '/api/v1/profile'  // Use relative URL for IIS proxy

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('access_token')
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, config)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  private async uploadFile(
    endpoint: string,
    file: File,
    options: RequestInit = {}
  ): Promise<ProfilePictureResponse> {
    const token = localStorage.getItem('access_token')
    
    const formData = new FormData()
    formData.append('file', file)

    const config: RequestInit = {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      body: formData,
      ...options,
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, config)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Get current user's profile
   */
  async getProfile(): Promise<ProfileData> {
    return this.request<ProfileData>('/me')
  }

  /**
   * Create a new profile
   */
  async createProfile(profileData: Partial<ProfileData>): Promise<ProfileData> {
    return this.request<ProfileData>('/', {
      method: 'POST',
      body: JSON.stringify(profileData),
    })
  }

  /**
   * Update current user's profile
   */
  async updateProfile(profileData: Partial<ProfileData>): Promise<ProfileData> {
    return this.request<ProfileData>('/', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    })
  }

  /**
   * Upload profile picture
   */
  async uploadProfilePicture(file: File): Promise<ProfilePictureResponse> {
    return this.uploadFile('/picture', file)
  }

  /**
   * Delete profile picture
   */
  async deleteProfilePicture(): Promise<{ message: string }> {
    return this.request<{ message: string }>('/picture', {
      method: 'DELETE',
    })
  }

  /**
   * Get profile completion percentage
   */
  async getProfileCompletion(): Promise<ProfileCompletion> {
    return this.request<ProfileCompletion>('/completion')
  }

  /**
   * Delete current user's profile
   */
  async deleteProfile(): Promise<{ message: string }> {
    return this.request<{ message: string }>('/', {
      method: 'DELETE',
    })
  }
}

export const profileAPI = new ProfileAPI()
